// Globally-accessible Stripe TEST/LIVE toggle. Used in both the Layout
// header (compact variant) and the Coins page header (full variant with
// inline warnings). Both render the same dialog when switching to LIVE.
//
// Source of truth is server-side (/admin/stripe/mode). Multiple instances
// of this component happily co-exist — each fetches on mount and refreshes
// after a successful toggle. There's no cross-instance event bus, so if
// admin flips it on the Coins page the badge in Layout won't update until
// next mount/route change. Acceptable for an admin tool.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Switch,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import api from '../config/api';

const fontFamily = "'Poppins', sans-serif";

export default function StripeModeToggle({
  // 'compact' = used in Layout header. 'full' = used in Coins page (shows
  // an extra label + missing-key warning inline).
  variant = 'compact',
  onChange,
  // Endpoint + copy are parameterized so this same component drives both the
  // coin-payment toggle (default) and the Stripe Identity toggle.
  endpoint = '/admin/stripe/mode',
  label = 'Stripe mode',
  tooltipLive = 'Stripe is in LIVE mode — real cards are charged',
  tooltipTest = 'Stripe is in TEST mode — only test cards work',
  confirmTitle = 'Switch to LIVE mode?',
  confirmBody = 'Every Shop purchase will charge a real card from now on. Stripe test cards (e.g. 4242 4242 4242 4242) will be declined.',
  missingKeysText = "Some Stripe keys for %MODE% mode aren't set on the server. Purchases will fail until they are.",
}) {
  const [mode, setMode] = useState(null);
  const [configured, setConfigured] = useState({ test: {}, live: {} });
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoint);
      const data = response.data.data || response.data;
      setMode(data.mode || 'test');
      setConfigured(data.configured || { test: {}, live: {} });
      setError('');
    } catch (e) {
      console.error('Failed to load Stripe mode:', e);
      setError('Failed to load Stripe mode.');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const applyMode = async (nextMode) => {
    try {
      setSwitching(true);
      await api.put(endpoint, { mode: nextMode });
      setMode(nextMode);
      setConfirmOpen(false);
      onChange?.(nextMode);
    } catch (e) {
      console.error('Failed to switch Stripe mode:', e);
      setError(e.response?.data?.message || 'Failed to switch Stripe mode.');
    } finally {
      setSwitching(false);
    }
  };

  const requestToggle = (nextMode) => {
    if (nextMode === mode || switching) return;
    if (nextMode === 'live') {
      setConfirmOpen(true);
      return;
    }
    applyMode('test');
  };

  const isLive = mode === 'live';
  const liveKeysMissing =
    !configured.live?.secretKey ||
    !configured.live?.publishableKey ||
    !configured.live?.webhookSecret;
  const testKeysMissing =
    !configured.test?.secretKey ||
    !configured.test?.publishableKey ||
    !configured.test?.webhookSecret;

  const missingForCurrent = isLive ? liveKeysMissing : testKeysMissing;

  // Compact variant: small pill with chip + switch, fits in the top app bar.
  if (variant === 'compact') {
    return (
      <>
        <Tooltip
          arrow
          title={
            loading
              ? 'Loading…'
              : isLive
              ? tooltipLive
              : tooltipTest
          }
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              py: 0.5,
              borderRadius: '999px',
              border: '1px solid',
              borderColor: isLive ? '#E53E3E' : '#E2E8F0',
              background: isLive ? 'rgba(229,62,62,0.06)' : '#F7FAFC',
              minHeight: 32,
            }}
          >
            {loading ? (
              <CircularProgress size={14} sx={{ color: '#A0AEC0' }} />
            ) : (
              <>
                <Chip
                  size="small"
                  label={isLive ? 'LIVE' : 'TEST'}
                  sx={{
                    fontFamily,
                    fontWeight: 700,
                    fontSize: 10,
                    letterSpacing: 0.4,
                    background: isLive ? '#E53E3E' : '#3FA477',
                    color: '#fff',
                    height: 20,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
                <Switch
                  size="small"
                  checked={isLive}
                  disabled={switching}
                  onChange={(e) => requestToggle(e.target.checked ? 'live' : 'test')}
                  sx={{
                    transform: 'scale(0.85)',
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#E53E3E' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#E53E3E',
                    },
                  }}
                />
              </>
            )}
          </Box>
        </Tooltip>
        {renderConfirmDialog()}
      </>
    );
  }

  // Full variant: bigger pill with label, used on the Coins page.
  // Everything is wrapped in ONE column container so that, when placed inside a
  // parent flex row, the pill + any warning/error Alert count as a single flex
  // item — the warnings stack *below* the pill instead of reflowing the row.
  return (
    <>
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 1, maxWidth: 460 }}>
        <Box
          sx={{
            alignSelf: 'flex-start',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 0.5,
            pr: 1.5,
            borderRadius: '999px',
            border: '1px solid',
            borderColor: isLive ? '#E53E3E' : '#EDF2F7',
            background: isLive ? 'rgba(229,62,62,0.05)' : '#FAFBFC',
          }}
        >
          <Chip
            size="small"
            label={loading ? '…' : isLive ? 'LIVE' : 'TEST'}
            sx={{
              fontFamily,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.5,
              background: isLive ? '#E53E3E' : '#3FA477',
              color: '#fff',
              height: 26,
            }}
          />
          <Switch
            checked={isLive}
            disabled={loading || switching}
            onChange={(e) => requestToggle(e.target.checked ? 'live' : 'test')}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#E53E3E' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#E53E3E',
              },
            }}
          />
          <Typography sx={{ fontFamily, fontSize: 12, color: '#4A5568', fontWeight: 500 }}>
            {label}
          </Typography>
        </Box>
        {!loading && missingForCurrent && (
          <Alert severity="warning" sx={{ fontFamily, borderRadius: '10px', py: 0.25, fontSize: 12.5 }}>
            {missingKeysText.replace('%MODE%', isLive ? 'LIVE' : 'TEST')}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ fontFamily, borderRadius: '10px', py: 0.25, fontSize: 12.5 }}>
            {error}
          </Alert>
        )}
      </Box>
      {renderConfirmDialog()}
    </>
  );

  function renderConfirmDialog() {
    return (
      <Dialog
        open={confirmOpen}
        onClose={() => !switching && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily, fontWeight: 600, color: '#1A202C' }}>
          {confirmTitle}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily, fontSize: 14, color: '#1A202C', mb: 2 }}>
            {confirmBody}
          </Typography>
          {liveKeysMissing && (
            <Alert severity="warning" sx={{ fontFamily, fontSize: 13 }}>
              Some LIVE-mode env vars are missing on the server. Switching now will break purchases until they're set.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={switching}
            sx={{ fontFamily, textTransform: 'none', color: '#969696', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => applyMode('live')}
            disabled={switching}
            sx={{
              fontFamily,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '10px',
              px: 3,
              background: '#E53E3E',
              '&:hover': { background: '#C53030' },
            }}
          >
            {switching ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Switch to LIVE'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
